import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type ColorMode = 'light' | 'dark';
export type ThemeVariant = 'studio' | 'analytics';

export interface ThemeConfig {
  /** Light or dark color mode */
  colorMode: ColorMode;
  /** Theme variant (studio = default, analytics = new data-focused theme) */
  variant: ThemeVariant;
}

export interface ThemeContextValue {
  /** Current theme configuration */
  theme: ThemeConfig;
  /** Whether dark mode is active */
  isDarkMode: boolean;
  /** Whether analytics theme is active */
  isAnalyticsTheme: boolean;
  /** Toggle between light and dark mode */
  toggleColorMode: () => void;
  /** Set specific color mode */
  setColorMode: (mode: ColorMode) => void;
  /** Toggle between studio and analytics theme */
  toggleVariant: () => void;
  /** Set specific theme variant */
  setVariant: (variant: ThemeVariant) => void;
  /** Set full theme config */
  setTheme: (config: Partial<ThemeConfig>) => void;
  /** CSS classes to apply to the root element */
  themeClasses: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const THEME_STORAGE_KEY = 'pray-studio-theme';

const DEFAULT_THEME: ThemeConfig = {
  colorMode: 'light',
  variant: 'studio',
};

// =============================================================================
// CONTEXT
// =============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access and control the current theme.
 * Must be used within a ThemeProvider.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Safe version of useTheme that returns defaults when outside provider.
 * Useful for components that may be rendered outside the theme context.
 */
export function useThemeSafe(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: DEFAULT_THEME,
      isDarkMode: false,
      isAnalyticsTheme: false,
      toggleColorMode: () => {},
      setColorMode: () => {},
      toggleVariant: () => {},
      setVariant: () => {},
      setTheme: () => {},
      themeClasses: 'theme-studio theme-light light',
    };
  }
  return context;
}

// =============================================================================
// PROVIDER
// =============================================================================

export interface ThemeProviderProps {
  children: ReactNode;
  /** Initial theme config (used when no localStorage value exists) */
  defaultTheme?: Partial<ThemeConfig>;
  /** Whether to persist theme to localStorage */
  persist?: boolean;
  /** Whether to apply theme classes to document.body */
  applyToBody?: boolean;
}

/**
 * Provider component for theme management.
 * Handles theme state, persistence, and applying CSS classes.
 */
export function ThemeProvider({ children, defaultTheme, persist = true, applyToBody = true }: ThemeProviderProps) {
  // Initialize state from localStorage or defaults
  // Priority: localStorage (if persist=true and exists) > defaultTheme > DEFAULT_THEME
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    // Build the base theme from defaults
    const baseTheme = { ...DEFAULT_THEME, ...defaultTheme };

    if (typeof window === 'undefined') {
      return baseTheme;
    }

    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && persist) {
        const parsed = JSON.parse(stored);
        // Restore only variant preference — colorMode always starts as light (issue #2944)
        return { ...baseTheme, ...(parsed.variant ? { variant: parsed.variant } : {}) };
      }
    } catch {
      // Ignore localStorage errors
    }

    return baseTheme;
  });

  // Derive computed values
  const isDarkMode = theme.colorMode === 'dark';
  const isAnalyticsTheme = theme.variant === 'analytics';

  // Build theme classes string for body element
  // Applies both theme variant (studio/analytics) and color mode (light/dark)
  const themeClasses = useMemo(() => {
    const classes: string[] = [];

    // Theme variant class (studio or analytics)
    classes.push(`theme-${theme.variant}`);

    // Color mode class
    classes.push(isDarkMode ? 'theme-dark' : 'theme-light');
    classes.push(isDarkMode ? 'dark' : 'light');

    return classes.join(' ');
  }, [theme.variant, isDarkMode]);

  // Persist theme changes
  useEffect(() => {
    if (persist && typeof window !== 'undefined') {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [theme, persist]);

  // Apply theme classes to html and body (variant + color mode)
  // Classes on <html> override :root dark defaults at the same cascade level.
  // Classes on <body> activate semantic tokens (.theme-studio.theme-light).
  useLayoutEffect(() => {
    if (!applyToBody || typeof document === 'undefined') return;

    const { body, documentElement } = document;
    const themeClassList = ['theme-studio', 'theme-analytics', 'theme-light', 'theme-dark', 'light', 'dark'];

    const applyTheme = () => {
      documentElement.classList.remove(...themeClassList);
      body.classList.remove(...themeClassList);
      themeClasses.split(' ').forEach((cls) => {
        if (cls) {
          documentElement.classList.add(cls);
          body.classList.add(cls);
        }
      });
    };

    applyTheme();

    // Guard against external mutations (unknown code adding theme-dark to body)
    let isApplying = false;
    const observer = new MutationObserver(() => {
      if (isApplying) return;
      const expectedClasses = new Set(themeClasses.split(' ').filter(Boolean));
      const needsCorrection = themeClassList.some((cls) => {
        return expectedClasses.has(cls) !== body.classList.contains(cls);
      });
      if (needsCorrection) {
        isApplying = true;
        applyTheme();
        isApplying = false;
      }
    });

    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    observer.observe(documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, [themeClasses, applyToBody]);

  // Theme control functions
  const toggleColorMode = useCallback(() => {
    setThemeState((prev) => ({
      ...prev,
      colorMode: prev.colorMode === 'light' ? 'dark' : 'light',
    }));
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => {
    setThemeState((prev) => ({ ...prev, colorMode: mode }));
  }, []);

  const toggleVariant = useCallback(() => {
    setThemeState((prev) => ({
      ...prev,
      variant: prev.variant === 'studio' ? 'analytics' : 'studio',
    }));
  }, []);

  const setVariant = useCallback((variant: ThemeVariant) => {
    setThemeState((prev) => ({ ...prev, variant }));
  }, []);

  const setTheme = useCallback((config: Partial<ThemeConfig>) => {
    setThemeState((prev) => ({ ...prev, ...config }));
  }, []);

  // Context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDarkMode,
      isAnalyticsTheme,
      toggleColorMode,
      setColorMode,
      toggleVariant,
      setVariant,
      setTheme,
      themeClasses,
    }),
    [
      theme,
      isDarkMode,
      isAnalyticsTheme,
      toggleColorMode,
      setColorMode,
      toggleVariant,
      setVariant,
      setTheme,
      themeClasses,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeProvider;
